import { PluginKey, Plugin } from '@tiptap/pm/state';
import { captureException } from '@fabric-app/compat/capabilities/sentry';
import { v4 } from 'uuid';
import { EMBED_RESOURCE_TYPE_NAME } from './constants.js';
import { createLogger } from '@fabric-app/utils/logging';
import { getWoodyClient } from '../../../woody/woody.js';
import { updateNodeAttributesWithResourceDetail } from './utils.js';
import { toast } from '../../../store/alerts.js';
import { handleMultipleEmbedUploads } from './handleUpload.js';
import { getCopiedResourceClipboardData } from '../../../resources/utils/resourceCopy/copyResourceToClipboard.js';
import { ensureError } from '@fabric-app/utils/guards';

const EmbedResourceDropHandlerKey = new PluginKey("EmbedResourceDropHandler");
const findNode = (view, uuid) => {
  let found = null;
  view.state.doc.descendants((node, pos) => {
    if (node.attrs["data-uuid"] === uuid && node.type.name === EMBED_RESOURCE_TYPE_NAME) {
      found = {
        node,
        pos
      };
    }
    if (found) return false;
  });
  return found;
};
const logger = createLogger("Paste drop");
const addExistingResource = async (view, resourceId, pos) => {
  let nodeId = null;
  try {
    const { schema } = view.state;
    const node = schema.nodes[EMBED_RESOURCE_TYPE_NAME].create({
      loading: true,
      alt: "Pasting Fabric link",
      resourceId: void 0,
      parentResourceId: void 0,
      uploadProgress: 0,
      ["data-uuid"]: v4()
    });
    nodeId = node.attrs["data-uuid"];
    logger.log("Add existing", node);
    if (!nodeId) {
      const transaction = view.state.tr.deleteRange(pos, pos + node.nodeSize);
      return transaction;
    } else {
      const createTransaction = view.state.tr.insert(pos, node);
      try {
        view.dispatch(createTransaction);
      } catch (e) {
        captureException(ensureError(e));
        return;
      }
    }
    const woodyClient = await getWoodyClient();
    const resource = await woodyClient.v2({
      endpoint: "/v2/resources/{resourceId}",
      params: {
        resourceId
      }
    }, {
      query: {
        includeHidden: true
      }
    });
    updateNodeAttributesWithResourceDetail(view, nodeId, resource);
  } catch (e) {
    captureException(ensureError(e));
    toast({
      content: "There was an with embedding an item."
    });
    if (!nodeId) return;
    const foundNode = findNode(view, nodeId);
    if (!foundNode) return;
    const { node, pos: pos2 } = foundNode;
    const transaction = view.state.tr.deleteRange(pos2, pos2 + node.nodeSize);
    view.dispatch(transaction);
  }
};
const NO_UPLOAD_ERROR_MESSAGE = "No onUpload function provided for embed notepad resource";
const FAILED_TO_RETRIEVE_DRAG_DROP_COORDINATES = "Failed to retrieve drag drop coordinates";
const createPasteDropPlugin = ({ onUpload, onCheckBeforeUpload, suppressMissingUploadError }) => {
  return new Plugin({
    key: EmbedResourceDropHandlerKey,
    state: {
      init: () => ({
        onUpload,
        onCheckBeforeUpload
      }),
      apply: (tr, pluginState) => {
        const change = tr.getMeta(EmbedResourceDropHandlerKey);
        if (change !== void 0) {
          pluginState = Object.assign({}, pluginState);
          for (const key in change) {
            pluginState[key] = change[key];
          }
        }
        return pluginState;
      }
    },
    props: {
      // since we have no other current drop handlers, we can just return true
      handleDOMEvents: {
        drop: (view, event) => {
          const onUpload2 = EmbedResourceDropHandlerKey.getState(view.state)?.onUpload;
          if (!onUpload2) {
            captureException(new Error(NO_UPLOAD_ERROR_MESSAGE));
            return false;
          }
          const files = Array.from(event.dataTransfer?.files || []);
          if (files.length === 0) {
            return false;
          }
          const onCheckBeforeUpload2 = EmbedResourceDropHandlerKey.getState(view.state)?.onCheckBeforeUpload;
          if (onCheckBeforeUpload2 && onCheckBeforeUpload2({
            files
          }) === "ABORT") {
            console.log("AAAABORT");
            event.preventDefault();
            event.stopPropagation();
            return false;
          }
          const coordinates = view.posAtCoords({
            left: event.clientX,
            top: event.clientY
          });
          if (!coordinates) {
            captureException(new Error(FAILED_TO_RETRIEVE_DRAG_DROP_COORDINATES));
            return false;
          }
          event.preventDefault();
          event.stopPropagation();
          handleMultipleEmbedUploads({
            files,
            view,
            pos: coordinates.pos,
            onUpload: onUpload2
          });
          return true;
        },
        paste: (view, event) => {
          const coordinates = view.posAtCoords(view.coordsAtPos(view.state.selection.to));
          if (!coordinates) return true;
          const pastedResources = getCopiedResourceClipboardData(event.clipboardData);
          if (pastedResources) {
            event.preventDefault();
            pastedResources.resources.forEach((resource) => {
              addExistingResource(view, resource.id, coordinates.pos);
            });
            return;
          }
          const onUpload2 = EmbedResourceDropHandlerKey.getState(view.state)?.onUpload;
          if (!onUpload2) {
            if (!suppressMissingUploadError) captureException(new Error(NO_UPLOAD_ERROR_MESSAGE));
            return false;
          }
          const files = Array.from(event.clipboardData?.files || []);
          if (files.length === 0) {
            return false;
          }
          const onCheckBeforeUpload2 = EmbedResourceDropHandlerKey.getState(view.state)?.onCheckBeforeUpload;
          if (onCheckBeforeUpload2 && onCheckBeforeUpload2({
            files
          }) === "ABORT") {
            event.preventDefault();
            event.stopPropagation();
            return false;
          }
          event.preventDefault();
          event.stopPropagation();
          handleMultipleEmbedUploads({
            files,
            view,
            pos: coordinates.pos,
            onUpload: onUpload2
          });
          return true;
        }
      }
    }
  });
};

export { EmbedResourceDropHandlerKey, addExistingResource, createPasteDropPlugin };
//# sourceMappingURL=PasteDropPlugin.js.map
